export const hullCleaningOutcomes = ["Hull Inspection", "Propeller Polishing", "Hull Cleaning"] as const;
export const hullCleaningSubOutcomes = ["Full Hull Cleaning", "Partial Hull Cleaning"] as const;

export type HullCleaningOutcome = (typeof hullCleaningOutcomes)[number];
export type HullCleaningSubOutcome = (typeof hullCleaningSubOutcomes)[number];

export const hullCleaningStatuses = ["OPEN", "PLANNED", "COMPLETED", "CANCELLED"] as const;

export type HullCleaningWorkflowStatus = (typeof hullCleaningStatuses)[number];

export const HULL_CLEANING_REQUEST_REASONS = [
  "Underperformance Observed",
  "Prolonged idle stay",
  "Underperformance & Prolonged idle stay",
  "Routine",
] as const;

export type HullCleaningRequestReason = (typeof HULL_CLEANING_REQUEST_REASONS)[number];

export interface HullCleaningRequestFormValue {
  imoNumber: string;
  reason: HullCleaningRequestReason | "";
  comment: string;
}

export type HullCleaningRequestFormErrors = Partial<Record<keyof HullCleaningRequestFormValue, string>>;

export interface CreateHullCleaningRequestInput {
  imoNumber: string;
  reason: HullCleaningRequestReason;
  comment: string;
}

export interface CreateHullCleaningRequestResponse {
  requestId: string;
  message: string;
  emailQueued: boolean;
}

export interface HullCleaningEvent {
  eventId: string;
  requestId: string;
  imoNumber: string | null;
  eventType: string;
  eventDate: string | null;
  eventPlace: string | null;
  comments: string | null;
  vendor: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface HullCleaningRequest {
  requestId: string;
  vesselCode: string;
  vesselName: string;
  imoNumber: string;
  opsCoordinatorName: string | null;
  opsCoordinatorEmail: string | null;
  requestDate: string | null;
  status: HullCleaningWorkflowStatus | string;
  reason: string | null;
  perfComment: string | null;
  cleaningDate: string | null;
  cleaningPort: string | null;
  bookingComment: string | null;
  bookingDate: string | null;
  outcomeDate: string | null;
  vendor: string | null;
  vendorRef: string | null;
  cancelComment: string | null;
  previousRequestId: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  outcomes: string[];
}

export interface HullCleaningRequestBundle {
  currentRequest: HullCleaningRequest;
  workflowTable: HullCleaningRequest[];
  completedEvents: HullCleaningEvent[];
}

export interface HullCleaningVendor {
  company: string;
  place: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  portName: string | null;
  preferred: boolean;
}

export interface HullCleaningVendorsResponse {
  vendors: HullCleaningVendor[];
}

export interface SubmitHullCleaningPlanningInput {
  requestId: string;
  imoNumber: string;
  vesselCode: string;
  opsCoordinatorName?: string;
  cleaningDate: string;
  cleaningPort: string;
  bookingComment?: string;
  updatedBy?: string;
}

export interface ConfirmHullCleaningBookingInput {
  requestId: string;
  imoNumber: string;
  vesselCode: string;
  updatedBy?: string;
}

export interface SubmitHullCleaningOutcomeInput {
  requestId: string;
  imoNumber: string;
  vesselCode: string;
  opsCoordinatorName?: string;
  cleaningDate: string;
  cleaningPort: string;
  vendor: string;
  vendorRef?: string;
  comments?: string;
  outcomes: HullCleaningOutcome[] | string[];
  hullCleaningType?: HullCleaningSubOutcome | string;
  updatedBy?: string;
}

export interface UpdateHullCleaningRequestInput {
  requestId: string;
  imoNumber: string;
  vesselCode: string;
  opsCoordinatorName?: string;
  cleaningDate?: string;
  cleaningPort?: string;
  bookingComment?: string;
  vendor?: string;
  vendorRef?: string;
  outcomes?: HullCleaningOutcome[] | string[];
  updatedBy?: string;
  updatedAt?: string;
}

export interface CancelHullCleaningRequestInput {
  requestId: string;
  imoNumber: string;
  vesselCode: string;
  cancelComment: string;
  updatedBy?: string;
}

export interface HullCleaningMutationResponse {
  success: boolean;
  requestId: string;
  status: HullCleaningWorkflowStatus | string;
  data: HullCleaningRequest;
}

export interface SubmitHullCleaningPlanningResponse extends HullCleaningMutationResponse {
  bookingDate: string | null;
}

export interface ConfirmHullCleaningBookingResponse extends HullCleaningMutationResponse {
  emailSent: boolean;
}

export interface SubmitHullCleaningOutcomeResponse extends HullCleaningMutationResponse {
  outcomeDate: string | null;
}

export interface UpdateHullCleaningRequestResponse extends HullCleaningMutationResponse {}

export interface CancelHullCleaningRequestResponse extends HullCleaningMutationResponse {}
